"use client"

import { useState, useEffect } from "react"
import { Input } from "#/components/ui/input"
import { Button } from "#/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "#/components/ui/avatar"
import { Card, CardContent } from "#/components/ui/card"
import { Search, X } from "lucide-react"
import { mockUsers, getCurrentUser } from "#/lib/mock-data"
import type { User } from "#/lib/types"
import Link from "next/link"

interface UserSearchProps {
  isOpen: boolean
  onClose: () => void
}

export function UserSearch({ isOpen, onClose }: UserSearchProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [followingUsers, setFollowingUsers] = useState<Set<string>>(new Set())
  const currentUser = getCurrentUser()

  useEffect(() => {
    if (searchQuery.trim()) {
      const results = mockUsers.filter(
        user =>
          user.id !== currentUser.id &&
          (user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.displayName.toLowerCase().includes(searchQuery.toLowerCase())),
      )
      setSearchResults(results)
    } else {
      // Show suggested users when no search query
      setSearchResults(
        mockUsers.filter(user => user.id !== currentUser.id).slice(0, 5),
      )
    }
  }, [searchQuery, currentUser.id])

  const handleFollowToggle = (userId: string) => {
    const newFollowing = new Set(followingUsers)
    if (newFollowing.has(userId)) {
      newFollowing.delete(userId)
    } else {
      newFollowing.add(userId)
    }
    setFollowingUsers(newFollowing)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-20">
      <Card className="w-full max-w-md">
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <CardContent className="p-0 max-h-96 overflow-y-auto">
          {searchResults.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No users found</p>
            </div>
          ) : (
            <div className="space-y-1">
              {searchResults.map(user => (
                <div
                  key={user.id}
                  className="flex items-center gap-3 p-4 hover:bg-muted/50"
                >
                  <Link
                    href={`/@${user.username}`}
                    className="flex items-center gap-3 flex-1"
                  >
                    <Avatar className="h-12 w-12">
                      <AvatarImage
                        src={user.avatar || "/placeholder.svg"}
                        alt={user.displayName}
                      />
                      <AvatarFallback>{user.displayName[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{user.username}</span>
                        {user.isVerified && (
                          <span className="text-primary text-xs">✓</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {user.displayName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {user.followerCount.toLocaleString()} followers
                      </p>
                    </div>
                  </Link>
                  <Button
                    size="sm"
                    variant={
                      followingUsers.has(user.id) ? "outline" : "default"
                    }
                    onClick={() => handleFollowToggle(user.id)}
                  >
                    {followingUsers.has(user.id) ? "Following" : "Follow"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
